import React, { useEffect, useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import useImages from 'api/useImages';
import useOffers from 'api/useOffers';
import useOffersUI from 'api/useOffersUI';
import usePermissions from 'api/usePermissions';
import { AxiosError } from 'axios';
import { Offer, OfferUI, RollingOfferProductSequence } from 'common/contracts';
import {
  MIN_ROLLING_SUB_OFFERS,
  MIN_USD_PRICE,
  PRICE_VALUE_LIMIT,
  TRIMMED_INPUT_REGEX
} from 'constants/constants';
import { ERollingOfferFormState, OfferType } from 'constants/enums';
import { useFormik } from 'formik';
import { motion } from 'framer-motion';
import { errorResponse } from 'utils/errorsTextHelper';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';
import * as yup from 'yup';

import FormHeader from 'components/FormHeader/FormHeader';
import FormSectionsTabs from 'components/FormSectionsTabs';
import { Tabs, TabsContent } from 'components/ui/tabs';

import { EToasterStatus, showToast } from '../../../utils/showToast';
import { DEFAULT_OFFER_DESIGN_EXTERNAL_ID } from '../constants';
import { RollingOfferFormProps } from '../types';
import { convertPriceFromCents, convertPriceToCents } from '../utils';

import RollingOfferMock from './Sections/Mock';
import RollingOfferModals from './Sections/Modals';
import ConfigurationTab from './Tabs/ConfigurationTab';
import ScheduleTab from './Tabs/ScheduleTab';
import SettingsTab from './Tabs/SettingsTab';

const RollingOfferForm: React.FC<RollingOfferFormProps> = ({
  edit = false,
  dup = false
}) => {
  const { publisherId } = useParams();
  const { rollingOfferId } = useParams();
  const navigate = useNavigate();
  const { canUserEdit } = usePermissions();

  const [data, setData] = useState<Partial<Offer>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [tab, setTab] = useState(ERollingOfferFormState.GENERAL);
  const [currentOfferUI, setCurrentOfferUI] = useState('');
  const { getOffersUI } = useOffersUI(publisherId, currentOfferUI);
  const [chosenSegment, setChosenSegment] = useState<string[]>([]);

  const { addOffer, updateOffer, getOffer, getOffers } = useOffers({
    offerId: rollingOfferId,
    offerType: OfferType.ROLLING_OFFER
  });
  const { getImages } = useImages(publisherId);
  const [productsSequence, setProductsSequence] = useState<
    RollingOfferProductSequence[]
  >([]);

  const checkUniqueness = (value: string | undefined, field: keyof Offer) => {
    const isTaken = getOffers.data?.offers.find((o: Offer) => {
      if (!edit) return o[field] === value;
      return o[field] === value && data[field] !== value;
    });
    return !isTaken;
  };

  const offerSchema = yup.object().shape({
    name: yup
      .string()
      .transform((value) => value.trim())
      .matches(TRIMMED_INPUT_REGEX, 'External ID should not be spaces')
      .required('Name is required')
      .test('uniqueness', 'Name already exists', (value) => {
        return checkUniqueness(value, 'name');
      }),
    offerUiId: yup.string().required('Offer Design is required'),
    publisherOfferId: yup
      .string()
      .transform((value) => value.trim())
      .matches(TRIMMED_INPUT_REGEX, 'External ID should not be spaces')
      .required('External ID is required')
      .test('uniqueness', 'External ID already exists', (value) => {
        return checkUniqueness(value, 'publisherOfferId');
      }),
    setAsFree: yup.boolean(),
    price: yup.number().when('setAsFree', {
      is: true,
      then: yup.number(),
      otherwise: yup
        .number()
        .required('price is required')
        .min(MIN_USD_PRICE, 'Price must be at least 0.8')
        .max(5000, 'Price must be at most 5000')
        .test('maxDigits', 'Price must have at most 10 digits', (value) =>
          value
            ? value.toString().replace('.', '').length <= PRICE_VALUE_LIMIT
            : true
        )
        .test(
          'maxDecimalPlaces',
          'Price must have at most 2 decimal points',
          (value) => {
            if (!value) return true;
            const decimalPart = value.toString().split('.')[1];
            return !decimalPart || decimalPart.length <= 2;
          }
        )
        .test(
          'maxIntegerPart',
          'Price integer part must have at most 8 digits',
          (value) => {
            if (!value) return true;
            const integerPart = value.toString().split('.')[0];
            return integerPart.length <= 8;
          }
        )
    }),
    priority: yup
      .number()
      .min(1, 'Priority must be at least 1')
      .max(10, 'Priority must be at most 10')
      .required('Priority is required'),
    priceDiscount: yup
      .number()
      .min(0, 'Price discount cannot be negative')
      .max(99, 'Price discount cannot be more than 99%'),
    productSale: yup.number().min(0, 'Sale amount cannot be negative')
  });

  const {
    values,
    handleChange,
    handleBlur,
    submitForm,
    isValid,
    dirty,
    errors,
    touched,
    setFieldValue,
    setTouched,
    validateField,
    validateForm
  } = useFormik({
    validateOnMount: true,
    validationSchema: offerSchema,
    enableReinitialize: true,
    initialValues: {
      active: edit ? data?.active : true,
      publisherOfferId: data.publisherOfferId || '',
      name: data.name ? `${data.name}${dup ? '_copy' : ''}` : '',
      type: OfferType.ROLLING_OFFER,
      priority: data.priority || 1,
      offerUiId: (edit || dup
        ? edit
          ? data.offerUi?._id || data.offerUi?.offerUiId || ''
          : (getOffer?.data?.result?.offerUi?.offerUiId as string) || ''
        : (getOffersUI?.data?.result?.find(
          (oi: OfferUI) => oi.externalId === DEFAULT_OFFER_DESIGN_EXTERNAL_ID
        )?._id as string) || ''
      ).toString(),
      segments: data.segments || [],
      schedule: data.schedule || {
        permanent: true,
        timeFrames: []
      },
      price: MIN_USD_PRICE,
      productsSequence:
        (data.productsSequence &&
          data.productsSequence.forEach((product: any) => ({
            ...product,
            priceInUsdCents: convertPriceFromCents(product?.priceInUsdCents || 0)
          }))) ||
        []
    },
    onSubmit: async (values) => {
      const newOffer: Partial<Offer> & { sectionId?: string | null } = {
        active: values.active,
        publisherOfferId: values.publisherOfferId,
        name: values.name,
        type: OfferType.ROLLING_OFFER,
        priority: values.priority,
        offerUiId: values.offerUiId,
        segments: chosenSegment,
        schedule: {
          ...values.schedule,
          timeFrames: values?.schedule?.permanent
            ? []
            : values?.schedule.timeFrames
        },
        productsSequence: productsSequence.length
          ? productsSequence.map((seq) => {
            const hasValidProducts = seq.products.some(
              (product) => product?.product?.productId
            );
            const products = hasValidProducts
              ? seq.products.map((product) => ({
                productId: product?.product?.productId,
                quantity: product.quantity,
                publisherProductId: product.product?.publisherProductId
              }))
              : [];
            return {
              index: seq.index || 1,
              products,
              displayName: seq.displayName || values.name,
              priceInUsdCents: convertPriceToCents(seq?.priceInUsdCents || 0),
              ...(seq?.priceDiscount?.discount !== 0 && {
                priceDiscount: seq?.priceDiscount
              }),
              ...(seq?.productSale?.sale !== 0 && {
                productSale: seq?.productSale
              }),
              ...(seq?.badges?.length && {
                badges: [
                  {
                    badgeId: seq.badges[0]?.badge?.badgeId,
                    publisherBadgeId: seq.badges[0]?.badge?.publisherBadgeId
                  }
                ]
              })
            };
          })
          : []
      };
      if (edit && rollingOfferId && !dup) {
        updateOffer.mutate(
          { offerId: rollingOfferId, form: newOffer },
          {
            onSuccess: () => {
              showToast({
                message: 'Rolling Offer edited successfully',
                status: EToasterStatus.SUCCESS
              });
              navigate('../');
            },
            onError: (error) => {
              if (error instanceof AxiosError) {
                showToast({
                  message: errorResponse(error),
                  status: EToasterStatus.ERROR
                });
              }
            }
          }
        );
      } else {
        addOffer.mutate(newOffer, {
          onSuccess: () => {
            showToast({
              message: 'New Rolling Offer added successfully',
              status: EToasterStatus.SUCCESS
            });
            navigate('../');
          },
          onError: (error) => {
            if (error instanceof AxiosError) {
              showToast({
                message: errorResponse(error),
                status: EToasterStatus.ERROR
              });
            }
          }
        });
      }
    }
  });

  useEffect(() => {
    if (!!rollingOfferId && !getOffer.isLoading && getOffer.data?.result) {
      setData(getOffer.data.result);
    }
  }, [rollingOfferId, getOffer?.data, getOffer?.isLoading]);

  useEffect(() => {
    if (!data) return;
    setIsLoading(false);
    setCurrentOfferUI(data.offerUi?._id || '');
    if (
      data.productsSequence &&
      (data.productsSequence as RollingOfferProductSequence[])
    ) {
      setProductsSequence(
        (data.productsSequence as RollingOfferProductSequence[]).map(
          (item) => ({
            ...item,
            priceInUsdCents: convertPriceFromCents(item?.priceInUsdCents || 0)
          })
        ) || []
      );
    }
  }, [data]);

  useEffect(() => {
    if (!productsSequence?.length) {
      const initialProductsSequence = Array.from(
        { length: MIN_ROLLING_SUB_OFFERS },
        (_, i) => ({
          index: i + 1,
          priceInUsdCents: 0,
          displayName: values.name,
          products: [
            {
              product: {
                images: []
              }
            }
          ]
        })
      );
      setProductsSequence(initialProductsSequence);
    }
  }, [productsSequence, setProductsSequence, values.name]);

  const hasMissingProductDetails = useMemo(() => {
    return productsSequence.some(
      ({ products }) =>
        !products.length ||
        products.some(
          ({ product, quantity }) => !product?.publisherProductId || !quantity
        )
    );
  }, [productsSequence]);

  useEffect(() => {
    setCurrentOfferUI(values.offerUiId);
  }, [values.offerUiId]);

  const offerDesignItems = useMemo(
    () =>
      getOffersUI.data?.result
        .filter(
          (oi: OfferUI) => oi.offerUiType === OfferDesignTypes.RollingOffer
        )
        .map((offerUI: OfferUI) => ({
          content: offerUI.name,
          key: offerUI._id,
          value: offerUI._id
        })) || [],
    [getOffersUI?.data?.result]
  );

  const defaultOfferDesignId = useMemo(
    () =>
      getOffersUI?.data?.result
        ? getOffersUI?.data?.result.find(
          (oi: OfferUI) => oi.externalId === DEFAULT_OFFER_DESIGN_EXTERNAL_ID
        )?._id
        : '',
    [getOffersUI?.data?.result]
  );

  useEffect(() => {
    if (!rollingOfferId) {
      setCurrentOfferUI(defaultOfferDesignId);
    }
  }, [defaultOfferDesignId, rollingOfferId]);

  const formikProps = {
    values,
    handleChange,
    handleBlur,
    submitForm,
    isValid,
    dirty,
    errors,
    touched,
    setFieldValue,
    setTouched,
    validateField,
    validateForm
  };

  const handleTabChange = (value: string) => {
    setTab(value as ERollingOfferFormState);
  };

  const updateContent = async () => {
    if (!hasMissingProductDetails && submitForm) {
      await submitForm();
    } else {
      if (edit && values.active) {
        setIsUpdateDialogOpen(true);
      } else {
        setIsSaveDialogOpen(true);
      }
    }
  };

  const formTitle = `${edit ? 'Edit' : 'Create'} Rolling Offer${edit && getOffer.data?.result?.name ? ` (${getOffer.data.result.name})` : ''}`;

  const isFormValid = !isLoading &&
    isValid &&
    canUserEdit &&
    (values.schedule?.permanent || values.schedule?.timeFrames?.length > 0) &&
    productsSequence.length >= MIN_ROLLING_SUB_OFFERS &&
    (!edit || dirty);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="bg-white fixed h-screen w-full top-0 left-0 z-[1000] overflow-hidden"
      >
        <FormHeader
          closeFormOnSubmit={false}
          onCloseForm={() => navigate('../')}
          onMainButtonClick={() => updateContent()}
          title={formTitle}
          isFormValid={isFormValid}
          mainButtonText="Save"
          secondaryButtonText={showPreview ? 'Hide Preview' : 'Show Preview'}
          onSecondaryButtonClick={() => setShowPreview(!showPreview)}
        />
        <div className="flex h-[calc(100vh-73px)]">
          <div className={`flex justify-center overflow-y-auto px-3 ${showPreview ? 'w-1/2' : 'w-full'}`}>
            {!getImages.isLoading && getImages.data && (
              <Tabs className="h-full pt-10 pb-10 w-[528px]" value={tab} onValueChange={handleTabChange}>
                <FormSectionsTabs
                  currentTab={tab}
                  tabs={[
                    {
                      value: ERollingOfferFormState.GENERAL,
                      label: 'Settings',
                      disabled: getOffer.isLoading && edit
                    },
                    {
                      value: ERollingOfferFormState.CONFIGURATION,
                      label: 'Configuration',
                      disabled: getOffer.isLoading && edit
                    },
                    {
                      value: ERollingOfferFormState.SCHEDULE,
                      label: 'Schedule',
                      disabled: getOffer.isLoading && edit
                    }
                  ]}
                />
                <div className='flex-1 flex justify-center'>
                  <TabsContent value={ERollingOfferFormState.GENERAL} className='mt-9 w-full'>
                    <SettingsTab
                      formikProps={formikProps}
                      rollingOfferId={rollingOfferId}
                      edit={edit}
                      offerDesignItems={offerDesignItems}
                      chosenSegment={chosenSegment}
                      setChosenSegment={setChosenSegment}
                      data={data}
                      productsSequence={productsSequence}
                      setProductsSequence={setProductsSequence}
                      dup={dup}
                    />
                  </TabsContent>
                  <TabsContent value={ERollingOfferFormState.CONFIGURATION} className='mt-9 w-full'>
                    <ConfigurationTab
                      formikProps={formikProps}
                      rollingOfferId={rollingOfferId}
                      productsSequence={productsSequence}
                      setProductsSequence={setProductsSequence}
                    />
                  </TabsContent>
                  <TabsContent value={ERollingOfferFormState.SCHEDULE} className='mt-9 w-full'>
                    <ScheduleTab
                      formikProps={formikProps}
                      rollingOfferId={rollingOfferId}
                      edit={edit}
                    />
                  </TabsContent>
                </div>
              </Tabs>
            )}
          </div>
          {showPreview && (
            <div className="w-1/2 bg-base-muted flex items-center justify-center">
              <RollingOfferMock
                currentOfferUI={currentOfferUI}
                productsSequence={productsSequence}
              />
            </div>
          )}
        </div>
      </motion.div>
      <RollingOfferModals
        isSaveDialogOpen={isSaveDialogOpen}
        isUpdateDialogOpen={isUpdateDialogOpen}
        setIsSaveDialogOpen={setIsSaveDialogOpen}
        setIsUpdateDialogOpen={setIsUpdateDialogOpen}
        submitForm={submitForm}
      />
    </>
  );
};

export default RollingOfferForm;
